import { coverageDashboardPanel } from '../admin/templates';
import type {
  AdminCoverageDashboard,
  AssignmentSectionSummary,
  AssignmentSummary,
  IdentityCandidateResponse,
  IdentityPersonSnapshot,
  IdentityVerificationStartResponse,
  LeaderDashboardResponse,
  TeamMember,
  VolunteerBootstrap
} from '../domain/types';

import type { CoveragePersonRole } from '../domain/types';

function storedAccessRoles(): CoveragePersonRole[] {
  try {
    const raw = window.localStorage.getItem('buckeyeCoverageIdentitySession');
    if (!raw) return [];
    const parsed = JSON.parse(raw) as { person?: { role?: CoveragePersonRole; roles?: CoveragePersonRole[] } };
    if (Array.isArray(parsed.person?.roles)) return parsed.person.roles;
    return parsed.person?.role ? ['USER', parsed.person.role] : [];
  } catch {
    return [];
  }
}

function accessSwitcherTemplate(): string {
  const roles = storedAccessRoles();
  if (!roles.length) return '';
  const links = [
    roles.includes('ADMIN') ? '<a href="/analytics">Analytics</a><a href="/admin">Admin</a>' : '',
    roles.includes('LEADER') ? '<a href="/leader">Leader</a>' : '',
    '<a href="/">Volunteer</a>'
  ].filter(Boolean).join('');
  return `<details class="access-switcher"><summary>View as</summary><div class="access-switcher-menu">${links}<button data-refresh-app type="button">Refresh app</button><button data-identity-sign-out type="button">Sign out</button></div></details>`;
}

export type VolunteerAssignmentChoice = 'AUTO' | 'PICK' | '';

export interface FormState {
  neighborhoodId: string;
  stacks: number;
  assignmentChoice: VolunteerAssignmentChoice;
}

export type AssignmentViewMode = 'current' | 'master';

function escapeHtml(value: string): string {
  return value.replace(/[&<>'"]/g, (character) => {
    const replacements: Record<string, string> = {
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      "'": '&#39;',
      '"': '&quot;'
    };
    return replacements[character] ?? character;
  });
}

function firstName(displayName: string): string {
  return displayName.trim().split(/\s+/)[0] || displayName.trim();
}

export function shellTemplate(content: string, _mode: 'mock' | 'api-preview' | 'live' | 'loading'): string {
  return `
    <div class="app-shell">
      <header class="app-header">
        <a class="brand-block brand-link" href="/">
          <div class="brand-mark" aria-hidden="true">B</div>
          <div><div class="brand-title">Buckeye Coverage</div><div class="brand-subtitle">Every home. Clear ownership.</div></div>
        </a>
        <div class="public-header-actions">
          ${accessSwitcherTemplate()}
        </div>
      </header>
      <main class="app-main">${content}</main>
    </div>
  `;
}

export function loadingTemplate(message = 'Loading neighborhoods and assignment options…'): string {
  return `
    <section class="center-panel" aria-live="polite" aria-busy="true">
      <div class="spinner" aria-hidden="true"></div>
      <h1>Opening Buckeye Coverage</h1>
      <p>${escapeHtml(message)}</p>
    </section>
  `;
}

export function identityPinTemplate(portal: 'USER' | 'LEADER' | 'ADMIN', message = ''): string {
  const portalLabel = portal === 'ADMIN' ? 'Admin sign in' : portal === 'LEADER' ? 'Leader sign in' : 'Volunteer sign in';
  return `
    <section class="identity-stage">
      <div class="identity-card">
        <p class="eyebrow">${portalLabel}</p>
        <h1>Enter the last 4 digits of your phone number</h1>
        <form id="identity-pin-form" novalidate>
          <label class="sr-only" for="identity-last-four">Last four digits</label>
          <input id="identity-last-four" class="identity-pin-input" inputmode="numeric" autocomplete="one-time-code" pattern="[0-9]{4}" maxlength="4" placeholder="••••" required />
          <div class="keypad" aria-label="Number keypad">
            ${[1,2,3,4,5,6,7,8,9].map((number) => `<button type="button" data-pin-digit="${number}">${number}</button>`).join('')}
            <button type="button" data-pin-action="clear">Clear</button>
            <button type="button" data-pin-digit="0">0</button>
            <button type="button" data-pin-action="backspace" aria-label="Delete last digit">⌫</button>
          </div>
          <div id="identity-message" class="form-message${message ? '' : ' hidden'}" role="alert">${escapeHtml(message)}</div>
          <button id="identity-find-button" class="primary-button" type="submit">Find my name</button>
        </form>
        ${portal === 'USER' ? '' : '<a class="admin-back-link" href="/">← Volunteer page</a>'}
      </div>
    </section>
  `;
}

export function identityCandidatesTemplate(
  result: IdentityCandidateResponse,
  portal: 'USER' | 'LEADER' | 'ADMIN'
): string {
  const candidates = result.candidates.map((candidate) => `
    <label class="identity-person-option">
      <input type="radio" name="identity-person" value="${escapeHtml(candidate.id)}" />
      <span><strong>${escapeHtml(candidate.displayName)}</strong></span>
    </label>
  `).join('');
  const noMatches = result.candidates.length === 0
    ? `<div class="identity-empty"><strong>No matching name was found.</strong><p>You can add your name and verify your mobile number. An admin will be notified so they can add you to Planning Center if needed.</p><button id="identity-register-self" class="secondary-button" type="button">Add my name</button></div>`
    : '';
  return `
    <section class="identity-stage">
      <div class="identity-card identity-card-wide">
        <p class="eyebrow">Confirm your identity</p>
        <h1>Select your name</h1>
        <p>${portal === 'ADMIN' ? 'Only people with admin access are shown.' : portal === 'LEADER' ? 'Only people with leader access are shown.' : 'Tap your name. We will text a code to the mobile number on your Planning Center record.'}</p>
        <form id="identity-person-form">
          <div class="identity-person-list">${candidates}${noMatches}</div>
          <div id="identity-message" class="form-message hidden" role="alert"></div>
        </form>
        <div class="identity-secondary-actions">
          <button id="identity-try-again" class="text-button" type="button">Use different digits</button>
        </div>
      </div>
    </section>
  `;
}

export function identitySelfRegistrationTemplate(message = ''): string {
  return `
    <section class="identity-stage">
      <div class="identity-card">
        <p class="eyebrow">Volunteer sign in</p>
        <h1>Add your name</h1>
        <p>Enter your name and mobile number. We’ll text a code to verify the number. This creates a volunteer sign-in and notifies an admin.</p>
        <form id="identity-registration-form" novalidate>
          <label for="identity-registration-name">First and last name</label>
          <input id="identity-registration-name" class="identity-code-input" autocomplete="name" maxlength="80" required />
          <label for="identity-registration-phone">Mobile phone number</label>
          <input id="identity-registration-phone" class="identity-code-input" type="tel" inputmode="tel" autocomplete="tel" maxlength="24" required />
          <div id="identity-message" class="form-message${message ? '' : ' hidden'}" role="alert">${escapeHtml(message)}</div>
          <button id="identity-register-button" class="primary-button" type="submit">Text me a verification code</button>
        </form>
        <div class="identity-secondary-actions"><button id="identity-registration-back" class="text-button" type="button">Back to name search</button></div>
      </div>
    </section>
  `;
}

export function identityVerificationTemplate(result: IdentityVerificationStartResponse): string {
  return `
    <section class="identity-stage">
      <div class="identity-card">
        <p class="eyebrow">Phone verification</p>
        <h1>Check your phone</h1>
        <p>We sent a six-digit code to <strong>${escapeHtml(result.destination)}</strong>.</p>
        <form id="identity-verification-form" novalidate>
          <label for="identity-verification-code">Verification code</label>
          <input id="identity-verification-code" class="identity-code-input" inputmode="numeric" autocomplete="one-time-code" pattern="[0-9]{6}" maxlength="6" placeholder="••••••" required />
          <div id="identity-message" class="form-message hidden" role="alert"></div>
          <button id="identity-verify-button" class="primary-button" type="submit">Verify and continue</button>
        </form>
        <div class="identity-secondary-actions">
          <button id="identity-resend-code" class="text-button" type="button">Send a new code</button>
          <button id="identity-change-name" class="text-button" type="button">Choose a different name</button>
        </div>
      </div>
    </section>
  `;
}

export function formTemplate(
  bootstrap: VolunteerBootstrap,
  state: FormState,
  person: IdentityPersonSnapshot
): string {
  const stackButtons = bootstrap.stackOptions
    .map((stacks) => {
      const active = stacks === state.stacks ? ' active' : '';
      const pressed = stacks === state.stacks ? 'true' : 'false';
      return `<button class="stack-button${active}" type="button" data-stacks="${stacks}" aria-pressed="${pressed}">${stacks}</button>`;
    })
    .join('');
  const customValue = bootstrap.stackOptions.includes(state.stacks) ? '' : String(state.stacks);
  const autoActive = state.assignmentChoice === 'AUTO' ? ' active' : '';
  const pickActive = state.assignmentChoice === 'PICK' ? ' active' : '';
  const hasChoice = Boolean(state.assignmentChoice);
  const selectedNeighborhood = bootstrap.neighborhoods.find((neighborhood) => neighborhood.id === state.neighborhoodId && neighborhood.available);
  const neighborhoodResults = bootstrap.neighborhoods
    .filter((neighborhood) => neighborhood.available)
    .map((neighborhood) => `<button type="button" class="assignment-neighborhood-result hidden" data-volunteer-neighborhood-id="${escapeHtml(neighborhood.id)}" data-volunteer-neighborhood-name="${escapeHtml(neighborhood.name)}"><strong>${escapeHtml(neighborhood.name)}</strong><small>${neighborhood.availableRoutes.toLocaleString()} sections available</small></button>`)
    .join('');
  const neighborhoodPicker = `
    <div class="field field-neighborhood">
      <label for="volunteer-neighborhood-search">Neighborhood</label>
      <input id="volunteer-neighborhood-search" type="search" autocomplete="off" placeholder="Start typing a neighborhood" />
      <div id="volunteer-neighborhood-results" class="assignment-neighborhood-results hidden">${neighborhoodResults}</div>
      <div id="volunteer-selected-neighborhood" class="selected-neighborhood${selectedNeighborhood ? '' : ' hidden'}">${selectedNeighborhood ? `<span>Selected neighborhood</span><strong>${escapeHtml(selectedNeighborhood.name)}</strong><button id="clear-volunteer-neighborhood" type="button" class="text-button">Change</button>` : ''}</div>
    </div>
  `;

  const automaticFields = state.assignmentChoice === 'AUTO' ? `
    ${neighborhoodPicker}

    <fieldset class="field field-stacks">
      <legend>How many tract stacks?</legend>
      <div class="stack-grid" id="stack-grid">${stackButtons}</div>
      <label class="custom-stack-label" for="custom-stacks">Custom number</label>
      <input id="custom-stacks" class="custom-stack-input" type="number" inputmode="numeric" min="1" max="${bootstrap.maximumStacks}" value="${escapeHtml(customValue)}" placeholder="For example, 10" />
      <p class="field-help">The app will automatically choose this many nearby available sections. Each selected section needs one tract stack.</p>
    </fieldset>
  ` : '';

  const pickerFields = state.assignmentChoice === 'PICK' ? `
    <div class="volunteer-picker-neighborhood">${neighborhoodPicker}</div>
    <div class="volunteer-section-picker">
      <div class="volunteer-picker-heading">
        <div>
          <span>Pick exact sections</span>
          <strong>Tap the sections you want on the map</strong>
        </div>
        <button id="clear-volunteer-sections" class="text-button" type="button">Clear selection</button>
      </div>
      <div id="volunteer-section-picker-map" class="section-picker-map volunteer-section-picker-map">
        <div class="map-empty-state"><strong>Loading available sections…</strong><p>The map will show only published sections that are still available.</p></div>
      </div>
      <div id="volunteer-section-picker-summary" class="volunteer-section-picker-summary" aria-live="polite">
        <strong>0 sections selected</strong>
        <span>Select sections to see how many tract stacks you need.</span>
      </div>
    </div>
  ` : '';

  return `
    <section class="form-stage">
      <div class="identity-current-bar">
        <div><span>Signed in as</span><strong>${escapeHtml(person.displayName)}</strong>${person.role === 'LEADER' ? '<em>Leader</em>' : ''}</div>
        <button data-identity-sign-out class="text-button" type="button">Not you?</button>
      </div>
      <div class="intro-block">
        <p class="eyebrow">Volunteer assignment</p>
        <h1>Get your maps</h1>
        <p>Choose whether you want the app to assign nearby sections automatically or whether you want to pick the exact sections yourself.</p>
      </div>

      <div class="volunteer-assignment-choice" role="group" aria-label="How sections should be selected">
        <button class="volunteer-choice-card${autoActive}" type="button" data-volunteer-assignment-choice="AUTO" aria-pressed="${state.assignmentChoice === 'AUTO'}">
          <span class="volunteer-choice-icon" aria-hidden="true">→</span>
          <span><strong>Assign sections for me</strong><small>Choose the number of tract stacks. The app picks nearby available sections automatically.</small></span>
        </button>
        <button class="volunteer-choice-card${pickActive}" type="button" data-volunteer-assignment-choice="PICK" aria-pressed="${state.assignmentChoice === 'PICK'}">
          <span class="volunteer-choice-icon" aria-hidden="true">⌖</span>
          <span><strong>Let me pick sections</strong><small>Open the neighborhood map, tap the exact sections you want, and see how many stacks you need.</small></span>
        </button>
      </div>

      ${hasChoice ? `
        <form id="assignment-form" class="assignment-form assignment-form-identity volunteer-assignment-form ${state.assignmentChoice === 'PICK' ? 'volunteer-pick-form' : ''}" novalidate>
          ${automaticFields}
          ${pickerFields}
          <div id="form-message" class="form-message hidden" role="alert"></div>
          <button id="assign-button" class="primary-button" type="submit"${state.assignmentChoice === 'PICK' ? ' disabled' : ''}>
            <span>${state.assignmentChoice === 'PICK' ? 'Assign selected sections' : 'Assign my sections'}</span><span aria-hidden="true">→</span>
          </button>
        </form>
      ` : `
        <div class="volunteer-choice-prompt">Choose one of the two options above to continue.</div>
      `}

    </section>
  `;
}

export function assignmentPickerTemplate(
  assignments: AssignmentSummary[],
  person: IdentityPersonSnapshot
): string {
  const cards = assignments.map((item) => {
    const remaining = Math.max(0, item.sections.length - item.completedSectionCount);
    const source = item.assignmentSource === 'SELF'
      ? 'You checked this out'
      : item.assignmentSource === 'LEADER'
        ? `Assigned by ${escapeHtml(item.assignedBy.displayName)}${item.groupName ? ` for ${escapeHtml(item.groupName)}` : ''}`
        : 'Assigned by an admin';
    return `
      <button class="active-assignment-card" type="button" data-assignment-token="${escapeHtml(item.claimToken)}">
        <span><strong>${escapeHtml(item.neighborhoodName)}</strong><em>${remaining} section${remaining === 1 ? '' : 's'} remaining</em></span>
        <small>${source}</small>
      </button>
    `;
  }).join('');
  return `
    <section class="form-stage">
      <div class="identity-current-bar">
        <div><span>Signed in as</span><strong>${escapeHtml(person.displayName)}</strong></div>
        <button data-identity-sign-out class="text-button" type="button">Not you?</button>
      </div>
      <div class="intro-block">
        <p class="eyebrow">Your assigned maps</p>
        <h1>Choose an assignment</h1>
        <p>These unfinished maps were checked out by you or assigned to you by a leader or admin.</p>
      </div>
      <div class="active-assignment-list">${cards}</div>
      <p class="assignment-picker-note">Finish these assignments before checking out additional maps.</p>
    </section>
  `;
}

export function pickupStacksTemplate(assignment: AssignmentSummary): string {
  const stacks = assignment.requestedStacks || Math.ceil(assignment.estimatedDoors / 50);
  return `
    <section class="pickup-stage">
      <div class="identity-current-bar">
        <div><span>Checked out by</span><strong>${escapeHtml(assignment.checkedOutBy.displayName)}</strong></div>
        <button data-identity-sign-out class="text-button" type="button">Sign out</button>
      </div>
      <div class="pickup-card" aria-labelledby="pickup-title">
        <div class="pickup-check" aria-hidden="true">✓</div>
        <p class="eyebrow">Maps checked out</p>
        <h1 id="pickup-title">Pick up your stacks</h1>
        <p class="pickup-neighborhood">${escapeHtml(assignment.neighborhoodName)}</p>
        <div class="pickup-count" aria-label="${stacks} tract stack${stacks === 1 ? '' : 's'}">
          <strong>${stacks}</strong>
          <span>TRACT STACK${stacks === 1 ? '' : 'S'}</span>
        </div>
        <p class="pickup-instruction">Show this screen to the person handing out maps.</p>
        <button id="stacks-received" class="primary-button pickup-confirm-button" type="button">Stacks received</button>
      </div>
    </section>
  `;
}

export function assignmentDashboardTemplate(
  assignment: AssignmentSummary,
  viewMode: AssignmentViewMode,
  selectedSectionId?: string
): string {
  const currentSection = chooseSection(assignment, selectedSectionId);
  if (!currentSection) return congratulationsTemplate(assignment);
  const completed = assignment.completedSectionCount;
  const total = assignment.sections.length;
  const progress = total ? Math.round((completed / total) * 100) : 0;
  const sectionButtons = assignment.sections.map((section) => sectionButton(section, currentSection.id)).join('');
  const mapMarkup = viewMode === 'master'
    ? masterMapPanel(assignment)
    : sectionMapPanel(currentSection);
  const currentTabClass = viewMode === 'current' ? ' active' : '';
  const masterTabClass = viewMode === 'master' ? ' active' : '';

  return `
    <section class="assignment-stage session-stage">
      <div class="identity-current-bar"><div><span>Signed in as</span><strong>${escapeHtml(assignment.checkedOutBy.displayName)}</strong></div><button data-identity-sign-out class="text-button" type="button">Sign out</button></div>
      <div class="assignment-hero session-hero">
        <div>
          <p class="eyebrow">Your assignment</p>
          <h1>${escapeHtml(assignment.checkedOutBy.displayName)}</h1>
          <p>${escapeHtml(assignment.neighborhoodName)}</p>
          ${assignment.assignmentSource === 'SELF' ? '' : `<small class="assignment-source-note">Assigned by ${escapeHtml(assignment.assignedBy.displayName)}${assignment.groupName ? ` for ${escapeHtml(assignment.groupName)}` : ''}</small>`}
        </div>
        <div class="metric-row compact-metrics">
          <div class="metric-card"><strong>${assignment.estimatedDoors}</strong><span>assigned doors</span></div>
          <div class="metric-card"><strong>${total}</strong><span>sections</span></div>
          <div class="metric-card"><strong>${completed}</strong><span>complete</span></div>
        </div>
        <div class="progress-block" aria-label="${progress}% complete">
          <div class="progress-copy"><span>Assignment progress</span><strong>${completed} of ${total}</strong></div>
          <div class="progress-track"><span style="width:${progress}%"></span></div>
        </div>
      </div>

      <div class="view-tabs" role="tablist" aria-label="Assignment maps">
        <button id="current-view-tab" class="view-tab${currentTabClass}" type="button" role="tab" aria-selected="${viewMode === 'current'}">Current section</button>
        <button id="master-view-tab" class="view-tab${masterTabClass}" type="button" role="tab" aria-selected="${viewMode === 'master'}">Master map</button>
      </div>

      <div class="section-strip" aria-label="Assigned sections">${sectionButtons}</div>

      ${mapMarkup}

      <div class="session-footer-actions">
        <button id="back-to-assigned-maps" class="secondary-button" type="button">Your assigned maps</button>
        <button id="refresh-assignment" class="secondary-button" type="button">Refresh assignment</button>
      </div>
    </section>
  `;
}

export function teamModalTemplate(
  assignment: AssignmentSummary,
  section: AssignmentSectionSummary,
  teamMembers: TeamMember[]
): string {
  const chips = teamMembers.map((member, index) => `
    <span class="team-chip">
      ${escapeHtml(member.name)}
      <button type="button" data-remove-team-index="${index}" aria-label="Remove ${escapeHtml(member.name)}">×</button>
    </span>
  `).join('');

  return `
    <div class="modal-backdrop" id="team-modal-backdrop">
      <section class="modal-card" role="dialog" aria-modal="true" aria-labelledby="team-modal-title">
        <button id="close-team-modal" class="modal-close" type="button" aria-label="Close">×</button>
        <p class="eyebrow">Section ${section.sequence} of ${assignment.sections.length}</p>
        <h2 id="team-modal-title">Who is doing this section?</h2>
        <p>Search and select everyone delivering this section.</p>
        <div id="team-chip-list" class="team-chip-list">${chips}</div>
        <div class="person-search-field">
          <label for="team-member-name">Find a person</label>
          <input id="team-member-name" type="search" maxlength="120" autocomplete="off" placeholder="Start typing a name" />
          <div id="team-person-results" class="person-search-results" role="listbox"></div>
        </div>
        <div id="team-modal-message" class="form-message hidden" role="alert"></div>
        <div class="custom-name-action-row">
          <button id="add-custom-team-member" class="custom-name-pill hidden" type="button">Add name</button>
        </div>
        <div class="modal-actions">
          <button id="cancel-team-modal" class="secondary-button" type="button">Cancel</button>
          <button id="confirm-team" class="primary-button" type="button">Start section</button>
        </div>
      </section>
    </div>
  `;
}

export function congratulationsTemplate(assignment: AssignmentSummary): string {
  return `
    <section class="completion-stage">
      <div class="identity-current-bar"><div><span>Signed in as</span><strong>${escapeHtml(assignment.checkedOutBy.displayName)}</strong></div><button data-identity-sign-out class="text-button" type="button">Sign out</button></div>
      <div class="completion-mark" aria-hidden="true">✓</div>
      <p class="eyebrow">Assignment complete</p>
      <h1>Great job, ${escapeHtml(firstName(assignment.checkedOutBy.displayName))}!</h1>
      <p>You completed all ${assignment.sections.length} sections and approximately ${assignment.estimatedDoors} doors in ${escapeHtml(assignment.neighborhoodName)}.</p>
      <div class="metric-row completion-metrics">
        <div class="metric-card"><strong>${assignment.sections.length}</strong><span>sections complete</span></div>
        <div class="metric-card"><strong>${assignment.estimatedDoors}</strong><span>doors covered</span></div>
      </div>
      <button id="get-more-maps" class="primary-button" type="button">Get more maps</button>
      <button id="view-completed-master" class="secondary-button" type="button">View completed master map</button>
    </section>
  `;
}

export function leaderDashboardTemplate(
  dashboard: LeaderDashboardResponse,
  _bootstrap: VolunteerBootstrap,
  coverage: AdminCoverageDashboard
): string {
  const groupOptions = dashboard.groups.map((group) =>
    `<option value="${escapeHtml(group.id)}">${escapeHtml(group.name)} — ${group.memberCount} people</option>`
  ).join('');
  const ledGroupIds = new Set(dashboard.groups.map((group) => group.id));
  const assignments = dashboard.assignments.length
    ? dashboard.assignments.map((item) => {
      const mayReturn = item.status !== 'COMPLETE' && item.status !== 'ARCHIVED' &&
        Boolean(item.groupId && ledGroupIds.has(item.groupId));
      return `
        <article class="leader-assignment-card">
          <div><strong>${escapeHtml(item.assignedTo.displayName)}</strong><span class="status-chip status-${item.status.toLocaleLowerCase()}">${escapeHtml(item.status.replace('_', ' '))}</span></div>
          <p>${escapeHtml(item.neighborhoodName)} · ${item.estimatedDoors.toLocaleString()} doors · ${item.routeIds.length} section${item.routeIds.length === 1 ? '' : 's'}</p>
          <small>${item.assignmentSource === 'SELF' ? 'Self checkout' : `Assigned by ${escapeHtml(item.assignedBy.displayName)}`}${item.groupName ? ` · ${escapeHtml(item.groupName)}` : ''}</small>
          ${mayReturn ? `<button type="button" class="secondary-button compact-button leader-return-assignment" data-return-assignment-id="${escapeHtml(item.id)}" data-return-route-ids="${escapeHtml(item.routeIds.join(','))}">Take back all unfinished sections</button>` : ''}
        </article>
      `;
    }).join('')
    : '<div class="leader-empty"><strong>No assignments yet.</strong><p>Use the form above to assign maps to someone in a Planning Center group you lead.</p></div>';
  const assignCard = dashboard.groups.length ? `
    <section class="direct-assignment-card">
      <div><p class="eyebrow">Assign maps</p><h2>Assign sections reserved for your group</h2><p>Select a small group, start typing a member’s name, then click any sections an admin reserved for that group. The map shows all of the group’s available sections across neighborhoods.</p></div>
      <form id="leader-assignment-form" class="direct-assignment-form">
        <label for="leader-group">Small group</label>
        <select id="leader-group" required>${groupOptions}</select>
        <label for="leader-person-search">Group member</label>
        <input id="leader-person-search" type="search" autocomplete="off" placeholder="Start typing a group member’s name" />
        <div id="leader-person-results" class="person-search-results"></div>
        <div id="leader-selected-person" class="selected-person hidden"></div>
        <div id="leader-section-picker-summary" class="section-picker-summary"><strong>0 sections selected</strong><span>0 doors · 0 tract stacks</span></div>
        <div id="leader-assignment-section-map" class="section-picker-map"><div class="map-empty-state"><strong>Loading group sections…</strong><p>Only sections reserved by an admin for this group will appear.</p></div></div>
        <label class="allow-additional-row"><input id="leader-allow-additional" type="checkbox" /> <span>Assign more even when this person already has unfinished maps</span></label>
        <div id="leader-assignment-message" class="form-message hidden" role="status"></div>
        <button id="leader-assign-button" class="primary-button" type="submit" disabled>Assign selected sections</button>
      </form>
    </section>
  ` : `
    <section class="direct-assignment-card empty-direct-assignment">
      <p class="eyebrow">Assign maps</p><h2>No led groups are synced yet</h2><p>Ask an admin to sync Planning Center. This page will then show groups where you have a leader membership.</p>
    </section>
  `;
  return `
    <section class="leader-stage">
      <div class="identity-current-bar"><div><span>Leader</span><strong>${escapeHtml(dashboard.person.displayName)}</strong></div><button data-identity-sign-out class="text-button" type="button">Sign out</button></div>
      <div class="intro-block"><p class="eyebrow">Leader dashboard</p><h1>Assign and follow maps</h1><p>The map and totals show all published church territory. Analytics search is limited to groups you lead and people in those groups.</p></div>
      ${assignCard}
      ${coverageDashboardPanel(coverage, 'leader-master-coverage-map', 'leader-master-coverage-search', 'Analytics and data', 'All published Buckeye sections', true)}
      <div class="leader-history-heading"><p class="eyebrow">Assignment history</p><h2>Maps you manage</h2></div>
      <div class="leader-assignment-list">${assignments}</div>
    </section>
  `;
}


function chooseSection(assignment: AssignmentSummary, selectedSectionId?: string): AssignmentSectionSummary | undefined {
  if (selectedSectionId) {
    const selected = assignment.sections.find((section) => section.id === selectedSectionId);
    if (selected) return selected;
  }
  if (assignment.activeSectionId) {
    const active = assignment.sections.find((section) => section.id === assignment.activeSectionId);
    if (active) return active;
  }
  return assignment.sections.find((section) => section.status !== 'COMPLETE') ?? assignment.sections[0];
}

function sectionButton(section: AssignmentSectionSummary, selectedId: string): string {
  const selected = section.id === selectedId ? ' active' : '';
  const statusClass = section.status === 'COMPLETE'
    ? ' complete'
    : section.status === 'IN_PROGRESS'
      ? ' in-progress'
      : '';
  const icon = section.status === 'COMPLETE' ? '✓' : String(section.sequence);
  return `
    <button class="section-pill${selected}${statusClass}" type="button" data-section-id="${escapeHtml(section.id)}" aria-pressed="${section.id === selectedId}">
      <span>${icon}</span>
      <small>${section.doors} doors</small>
    </button>
  `;
}

function masterMapPanel(assignment: AssignmentSummary): string {
  const completedIds = new Set(assignment.sections.filter((section) => section.status === 'COMPLETE').map((section) => section.id));
  const list = assignment.sections.map((section) => `
    <li class="master-section-row${completedIds.has(section.id) ? ' complete' : ''}">
      <span>${completedIds.has(section.id) ? '✓' : section.sequence}</span>
      <div><strong>Section ${section.sequence}</strong><small>${section.doors} doors · ${escapeHtml(section.streetNames.join(', '))}</small></div>
    </li>
  `).join('');
  return `
    <div class="content-grid session-grid">
      <section class="map-card" aria-label="Master assignment map">
        <div class="map-toolbar"><div><strong>Master map</strong><span>All ${assignment.sections.length} assigned sections</span></div>${mapLegend()}</div>
        <div id="master-map" class="assignment-map session-map"></div>
        ${directionsRow(assignment.map.start)}
      </section>
      <section class="detail-card master-detail-card">
        <h2>All sections</h2>
        <ul class="master-section-list">${list}</ul>
      </section>
    </div>
  `;
}

function sectionMapPanel(section: AssignmentSectionSummary): string {
  const streets = section.streetNames.map((street) => `<li>${escapeHtml(street)}</li>`).join('');
  const team = section.teamMembers.length
    ? section.teamMembers.map((member) => `<span class="member-badge">${escapeHtml(member.name)}</span>`).join('')
    : '<span class="empty-team">Team not entered yet</span>';
  const savedComment = section.completionComment
    ? `<div class="section-saved-comment"><span>Comment</span><p>${escapeHtml(section.completionComment)}</p></div>`
    : '';
  const action = section.status === 'COMPLETE'
    ? `<div class="section-complete-banner">✓ This section is complete</div>${savedComment}`
    : section.status === 'IN_PROGRESS'
      ? `<label class="section-comment-field" for="section-completion-comment"><span>Anything we should know?</span><small>Optional — leave a note for the church team about this section.</small><textarea id="section-completion-comment" maxlength="2000" rows="3" placeholder="Example: gate was locked, several homes are still under construction, or follow-up may be helpful."></textarea></label><button id="complete-section" class="primary-button section-action" type="button">Complete this section</button>`
      : '<button id="start-section" class="primary-button section-action" type="button">Start this section</button>';
  const statusLabel = section.status === 'COMPLETE'
    ? 'Complete'
    : section.status === 'IN_PROGRESS'
      ? 'In progress'
      : 'Next to complete';

  return `
    <div class="content-grid session-grid">
      <section class="map-card" aria-label="Current section map">
        <div class="map-toolbar"><div><strong>Section ${section.sequence}</strong><span>${section.doors} doors · ${statusLabel}</span></div>${mapLegend()}</div>
        <div id="section-map" class="assignment-map session-map"></div>
        ${directionsRow(section.map.start)}
      </section>
      <section class="detail-card section-detail-card">
        <div class="section-status-line"><span class="status-dot ${section.status.toLowerCase().replace('_', '-')}"></span>${statusLabel}</div>
        <h2>Section ${section.sequence}</h2>
        <ul class="street-list">${streets}</ul>
        <div class="section-team-block"><h3>People doing this section</h3><div class="member-badge-list">${team}</div></div>
        ${action}
      </section>
    </div>
  `;
}

function directionsRow(start: { lat: number; lng: number } | undefined): string {
  if (!start) return '';
  return `<div class="directions-row">
    <a class="direction-button" href="https://maps.apple.com/?daddr=${start.lat},${start.lng}&dirflg=d" target="_blank" rel="noopener">Open in Apple Maps</a>
    <a class="direction-button" href="https://www.google.com/maps/dir/?api=1&destination=${start.lat},${start.lng}" target="_blank" rel="noopener">Open in Google Maps</a>
  </div>`;
}

function mapLegend(): string {
  return `<div class="map-legend" aria-label="Map legend">
    <span><i class="legend-line" aria-hidden="true"></i>Current</span>
    <span><i class="legend-complete" aria-hidden="true"></i>Complete</span>
    <span><i class="legend-door" aria-hidden="true"></i>Door</span>
    <span><i class="legend-reported-door" aria-hidden="true"></i>Reported location</span>
  </div>`;
}
